import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Container } from "../ui";
import { cn } from "../../utils/cn";

export interface FAQProps {
  className?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

const faqList: FAQItem[] = [
  {
    question: "Is SetGo just a discount app?",
    answer:
      "No, SetGo is an end-to-end local discovery and customer loyalty ecosystem. Beyond everyday and flash discounts, SetGo empowers customers to earn loyalty coins redeemable for vouchers, and provides merchants with verified in-store footfall, customer retention analytics, and area exclusivity.",
  },
  {
    question: "How do I redeem an offer?",
    answer:
      "Simply browse deals near you on the SetGo app, tap to redeem an offer, and walk into the partner store. Show your unique redemption verification code at the checkout counter. The merchant verifies it on their partner portal in seconds, applying your discount and instantly depositing your loyalty coins.",
  },
  {
    question: "What can I do with my coins?",
    answer:
      "Every verified redemption and visit earns you SetGo loyalty coins. You can accumulate these coins to level up through membership tiers (Silver, Gold, VIP) and swap them for exclusive store vouchers, gift cards, free products, and special partner experiences.",
  },
  {
    question: "What does area exclusivity mean for my business?",
    answer:
      "Area exclusivity ensures that your business stands out as SetGo's designated local partner for your specific service or retail category in your designated postal area. No direct competitors from the SetGo network will dilute your spotlight in that zone once claimed.",
  },
];

const FAQ: React.FC<FAQProps> = ({ className }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className={cn("py-16 sm:py-20 lg:py-28 bg-white relative overflow-hidden", className)}>
      <div className="absolute inset-0 bg-dot-grid pointer-events-none" aria-hidden="true" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[640px] max-w-full h-[420px] bg-purple-200/30 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <Container size="lg" padding="md" className="relative z-10">
        <motion.div
          className="text-center max-w-2xl mx-auto mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-[42px] font-black text-[#0F172A] tracking-tight">
            Questions, answered
          </h2>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqList.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={index}
                className={cn(
                  "glass-light rounded-2xl overflow-hidden transition-shadow duration-300",
                  isOpen
                    ? "ring-1 ring-purple-300/60 shadow-[0_20px_44px_-18px_rgba(76,29,149,0.3)]"
                    : "hover:shadow-[0_16px_36px_-18px_rgba(76,29,149,0.25)]"
                )}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <button
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 text-left focus:outline-none"
                  aria-expanded={isOpen}
                >
                  <span className="font-bold text-sm sm:text-base md:text-lg text-[#0F172A] pr-3 sm:pr-4">
                    {item.question}
                  </span>
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors duration-200 border",
                      isOpen
                        ? "bg-purple-600 text-white border-purple-600"
                        : "bg-white/80 text-purple-600 border-purple-100"
                    )}
                  >
                    <svg
                      className={cn("w-4 h-4 transition-transform duration-200", isOpen && "rotate-45")}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                    >
                      <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-1 text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed border-t border-purple-100/50">
                        {item.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

export { FAQ };
export default FAQ;
